import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { systemConfigKeyEnum } from '../../../../constants';
import { useCreateDailySubContractorMutation, useDailySubContractorMutation } from '../../../../hooks/mutations/useDailySubContractorMutation';
import { useSubContractorsQuery } from '../../../../hooks/queries/useSubContractorsQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DailySubContractor } from '../../../../services/apiSLTP/dailyReports/dailySubContractors';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageValidationDailySubContractorModalProps extends ModalProps {
    refetch: () => void;
    dailySubContractor: DailySubContractor | null;
    activities: OptionType[];
    siteName?: string;
}

const ManageValidationDailySubContractorModal: React.FC<ManageValidationDailySubContractorModalProps> = ({
    open,
    handleClose,
    refetch,
    dailySubContractor,
    activities,
    siteName,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentDailySubContractorId] = useState(dailySubContractor?._id || '');
    const [selectedSubContractorType, setSelectedSubContractorType] = useState({
        label: dailySubContractor?.subContractorTypeId.designation || '',
        value: dailySubContractor?.subContractorTypeId._id || '',
    });
    const [selectedActivity, setSelectedActivity] = useState({
        label: activities.find((activity) => activity.value === dailySubContractor?.activityId)?.label || '',
        value: dailySubContractor?.activityId || '',
    });
    const [includedInExpenses, setIncludedInExpenses] = useState<boolean>(dailySubContractor?.includedInExpenses ?? true);
    const [price, setPrice] = useState(dailySubContractor?.price?.toString() || '');

    const { data: dataSubContractors } = useSubContractorsQuery({ queries: { active: true } });

    const { mutate: createDailySubContractor } = useCreateDailySubContractorMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });
    const { mutate: updateDailySubContractor } = useDailySubContractorMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const postDailySubContractor = async () => {
        createDailySubContractor({
            body: {
                activityId: selectedActivity.value,
                subContractorTypeId: selectedSubContractorType.value,
                includedInExpenses,
                price: price.length ? parseFloat(price) : undefined,
            },
        });
    };

    const patchDailySubContractor = async () => {
        if (currentDailySubContractorId)
            updateDailySubContractor({
                id: currentDailySubContractorId,
                body: {
                    activityId: selectedActivity.value,
                    subContractorTypeId: selectedSubContractorType.value,
                    includedInExpenses,
                    price: price.length ? parseFloat(price) : undefined,
                },
            });
    };

    return (
        <ModalBody
            title={t('subContractor')}
            subtitle={!!currentDailySubContractorId.length ? t('update') : t('add')}
            description={siteName}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !currentDailySubContractorId.length ? postDailySubContractor() : patchDailySubContractor();
            }}
            disableSubmit={!selectedSubContractorType.value.length || !selectedActivity.value.length}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('activityOf')}
                required
            />
            <DropDown
                selectedItem={selectedSubContractorType}
                items={
                    dataSubContractors?.map((subContractor) => ({
                        label: subContractor.designation,
                        value: subContractor._id,
                    })) || []
                }
                handleChange={(event, value) => setSelectedSubContractorType({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('company')}
                required
            />

            <ToggleLine
                checked={includedInExpenses}
                onChange={(checked) => setIncludedInExpenses(checked)}
                title={capitalizeFirstLetter(t('includedInExpenses'))}
            />
            {includedInExpenses && (
                <CustomTextInput
                    value={price}
                    onChange={setPrice}
                    label={t('price')}
                    startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                    type="number"
                />
            )}
        </ModalBody>
    );
};

export default ManageValidationDailySubContractorModal;
